// Per-browser-session demo limits + counters.
//
// These live in sessionStorage (counters reset when the tab/session ends) and
// localStorage (UI prefs / onboarding). NO business data here — that's IndexedDB.

const COUNTER_KEY = "jf_demo_counters_v1";
const ONBOARDING_KEY = "jf_demo_onboarding_v1";

// Hard caps per browser session.
export const DEMO_LIMITS = {
  estimates: 5,
  repairs: 3,
  orders: 3,
  ai: 10,
  customers: 5, // custom customers a visitor may create (seeded ones don't count)
};

// Friendly labels for messages.
const LABELS = {
  estimates: "estimates",
  repairs: "repairs",
  orders: "orders",
  ai: "AI requests",
  customers: "customers",
};

function readCounters() {
  try {
    const raw = sessionStorage.getItem(COUNTER_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeCounters(counters) {
  try {
    sessionStorage.setItem(COUNTER_KEY, JSON.stringify(counters));
  } catch {
    // sessionStorage may be unavailable (private mode); limits just won't persist.
  }
}

export function used(kind) {
  const counters = readCounters();
  return Number(counters[kind] || 0);
}

export function remaining(kind) {
  const cap = DEMO_LIMITS[kind];
  if (cap == null) return Infinity;
  return Math.max(0, cap - used(kind));
}

export function canCreate(kind) {
  const cap = DEMO_LIMITS[kind];
  if (cap == null) return true;
  return used(kind) < cap;
}

export function recordCreate(kind) {
  const counters = readCounters();
  counters[kind] = Number(counters[kind] || 0) + 1;
  writeCounters(counters);
  return counters[kind];
}

export function limitMessage(kind) {
  const cap = DEMO_LIMITS[kind];
  const label = LABELS[kind] || kind;
  return `Demo limit reached — you can create up to ${cap} ${label} per session. Press “Reset demo” to start over.`;
}

export function resetCounters() {
  try {
    sessionStorage.removeItem(COUNTER_KEY);
  } catch {
    // ignore
  }
}

// --- onboarding / UI prefs (localStorage) ---
export function getOnboardingState() {
  try {
    const raw = localStorage.getItem(ONBOARDING_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function setOnboardingState(patch = {}) {
  try {
    const next = { ...getOnboardingState(), ...patch };
    localStorage.setItem(ONBOARDING_KEY, JSON.stringify(next));
    return next;
  } catch {
    return {};
  }
}
